import React, { useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Layout, Drawer, Typography, Divider } from "antd";
import { MenuOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  FaCog,
  FaQuestionCircle,
  FaSignOutAlt,
  FaPen,
  FaClipboardList,
  FaChartLine,
  FaComments,
  FaPeopleCarry,
} from "react-icons/fa";
import { hooks, selectors } from "farmx-redux-core";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { GEC_ENTITY_ID, GEC_TEST_ENTITY_ID } from "../../../constants";
import { ROUTES } from "../../app/routes";
import "./AppHeader.less";
import FarmXLogo from "../../../assets/images/farmx_logo_white_86.png";
import { Feedback } from "../feedback/Feedback";
import EntitySelect from "../../app/EntitySelect";
import { openTidioChat } from '../../app/TidioChat';
import '../helpers.css';

const { useEntities } = hooks;
const { selectLoginUserInfo } = selectors;

const logoutOption = {
  icon: FaSignOutAlt,
  text: "Logout",
  key: "logout",
  route: "/logout",
  tabIndex: "3",
};

const chatOption = {
  icon: FaComments,
  text: 'Chat with us',
  key: 'chat-with-us',
  tabIndex: '2',
};

const featurePages = [
  {
    icon: FaChartLine,
    text: "Graph",
    key: ROUTES.GRAPH.key,
    route: ROUTES.GRAPH.path,
    tabIndex: "0",
  },
  {
    icon: FaClipboardList,
    text: "Events",
    key: ROUTES.EVENTS.key,
    route: ROUTES.EVENTS.path,
    tabIndex: "1",
  },
];

const settings = [
  {
    icon: FaCog,
    text: "Settings",
    key: ROUTES.SETTINGS_PAGE.key,
    route: ROUTES.SETTINGS_PAGE.path,
    tabIndex: "0",
  },
  {
    icon: FaPeopleCarry,
    text: "Organization",
    key: ROUTES.ORGANIZATION_PAGE.key,
    route: ROUTES.ORGANIZATION_PAGE.path,
    tabIndex: "0",
  },
  {
    icon: FaQuestionCircle,
    text: 'Resources And Help',
    key: 'help',
    route: '/help',
    tabIndex: '1',
  },
  {
    icon: FaPen,
    text: "Submit Feedback",
    key: "SubmitFeedback",
    route: "/feedback",
    tabIndex: "2",
  },
  chatOption,
  logoutOption,
];

const farmhandSettings = [chatOption, logoutOption];

export const AppHeader = ({ alarms, appMode }) => {
  const userInfo = useSelector(selectLoginUserInfo).payload;
  const [settingsVisible, setSettingsVisible] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const [feedbackFormVisible, setFeedbackFormVisible] = useState(false);
  const { activeEntityId } = useEntities();
  const isGECEntity =
    (activeEntityId === GEC_ENTITY_ID || activeEntityId === GEC_TEST_ENTITY_ID )
    && !userInfo?.username.endsWith(".edu");
  const [isOpen, setIsOpen] = useState(false);

  const ruleSettingPages = [
    {
      text: t("Rule Settings"),
      key: ROUTES.RULE_SETTINGS.key,
      route: ROUTES.RULE_SETTINGS.path,
    },
    {
      text: t("Block Irrigations"),
      key: ROUTES.BLOCK_IRRIGATION_SETTINGS.key,
      route: ROUTES.BLOCK_IRRIGATION_SETTINGS.path,
    },
    {
      text: t("Global Settings"),
      key: ROUTES.GLOBAL_SETTINGS.key,
      route: ROUTES.GLOBAL_SETTINGS.path,
    },
  ];

  const onMenuClick = (menu) => {
    setSettingsVisible(false);
    if (menu.key === chatOption.key) openTidioChat();
    else if (menu.route.includes("/feedback")) setFeedbackFormVisible(true);
    else history.push(menu.route);
  };

  // appMode is not "farmhand"
  const isNormalUser = appMode === "default";

  // Decide what all options should be seen to user when sidebar is in open state
  const settingsToRender = isNormalUser ? settings : farmhandSettings;

  return (
    <>
      <Helmet>
        <title>FarmX</title>
      </Helmet>

      <Layout.Header>
        <div className="left-menu">
          <div className="mobile-menu-trigger">
            <MenuOutlined
              className="trigger"
              onClick={() => {
                setSettingsVisible(!settingsVisible);
              }}
            />
          </div>
        </div>

        <div className="logo">
          <img className="mobile-header-logo" alt="FarmX" src={FarmXLogo} />
        </div>

        <div className="right-menu">
          {isNormalUser ? (
            alarms
          ) : (
            <Typography.Text className="fieldHandTitle">
              {t("Fieldhand")}
            </Typography.Text>
          )}
        </div>
      </Layout.Header>

      <Drawer
        size="large"
        placement="left"
        closable
        onClose={() => {
          setSettingsVisible(false);
        }}
        visible={settingsVisible}
        getContainer={false}
        style={{ position: "absolute" }}
        headerStyle={{ display: "none" }}
        bodyStyle={{ marginTop: "50px" }}
      >
        <div className="mobile-side-menu">
          <EntitySelect collapse={() => setSettingsVisible(false)} />
          <Divider />
          {featurePages.map((s) => (
            <div
              key={s.key}
              className="mobile-side-menu-item"
              aria-hidden="true"
              onClick={() => onMenuClick(s)}
            >
              <div className="mobile-side-menu__icon">
                <s.icon />
              </div>
              <p className="mobile-side-menu__link">{t(`${s.text}`)}</p>
            </div>
          ))}
          {isGECEntity && (
            <>
              <div
                key="rule-settings"
                className="mobile-side-menu-item"
                aria-hidden="true"
                onClick={() => setIsOpen(!isOpen)}
              >
                <div className="mobile-side-menu__icon">
                  <FaCog />
                </div>
                <p className="mobile-side-menu__link uppercase">{t("Settings")}</p>
                <div className="submenu-icon">
                  {!isOpen ? <DownOutlined /> : <UpOutlined />}
                </div>
              </div>
              {isOpen && (
                <div className="submenu">
                  {ruleSettingPages.map((s) => (
                    <div
                      className="submenu-item"
                      aria-hidden="true"
                      onClick={() => onMenuClick(s)}
                    >
                      {t(`${s.text}`)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <Divider />
          {settingsToRender.map((s) => (
            <div
              key={s.key}
              className="mobile-side-menu-item"
              aria-hidden="true"
              onClick={() => onMenuClick(s)}
            >
              <div className="mobile-side-menu__icon">
                <s.icon />
              </div>
              <p className="mobile-side-menu__link">{t(`${s.text}`)}</p>
            </div>
          ))}
        </div>
      </Drawer>
      {feedbackFormVisible && (
        <Feedback onClick={(d) => setFeedbackFormVisible(d)} />
      )}
    </>
  );
};

AppHeader.defaultProps = {
  appMode: "default",
};

AppHeader.propTypes = {
  alarms: PropTypes.element.isRequired,
  appMode: PropTypes.string,
};
